import { useState, useEffect } from "react";
import { t } from "copy/t";
import { useGetGallerySessionToken } from "../api/mutations";
import Input from "components/TailwindComponents/Input";
import { getAccessibleTextColor, getContrastColor } from "utils/helpers";
import LegalLinks from "./LegalLinks";

/**
 * `onUnlocked`, when given, is called once the unlock token is stored, in place
 * of the full-page reload the gallery uses. The single-photo page passes it so
 * it can refetch that one photo with the new token without reloading.
 */
const GalleryGate = ({ channelId, galleryColor, onUnlocked }) => {
  const [password, setPassword] = useState("");
  const [error, setError] = useState(null);
  const [lockoutMessage, setLockoutMessage] = useState(null);
  const tokenMutation = useGetGallerySessionToken(channelId, password);

  const handleSubmit = async (event) => {
    event.preventDefault();
    tokenMutation.mutate();
  };

  useEffect(() => {
    if (tokenMutation.isSuccess) {
      const { data } = tokenMutation;
      if (data.error) {
        setError(data.error);
      } else if (data.lockoutMessage) {
        setLockoutMessage(data.lockoutMessage);
      } else if (data.success && data.unlock_token) {
        // V3 API returns { success: true, unlock_token: "..." }
        // Token is stored in mutation's onSuccess handler.
        // Hand control back to the caller, or reload the page to use the new token.
        if (onUnlocked) {
          onUnlocked();
        } else {
          window.location.reload();
        }
      } else {
        // Unexpected response shape — don't reload (would loop); show a generic error
        setError(t("galleryGate.unexpectedError"));
      }
    }
    // `data` is read above, so it has to be a dependency: a second submission
    // that resolves while `isSuccess` is still true would otherwise leave this
    // acting on the previous response.
    // `onUnlocked` is deliberately not a dependency: a parent re-render that
    // hands in a new callback must not re-fire the unlock for the same response.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tokenMutation.isSuccess, tokenMutation.data]);

  // Handle API errors (401 Unauthorized, 429 Too Many Requests, etc.)
  useEffect(() => {
    if (tokenMutation.isError) {
      const errorResponse = tokenMutation.error?.response?.data;
      if (errorResponse?.error) {
        // Check if it's a lockout error (429 Too Many Requests)
        if (tokenMutation.error?.response?.status === 429) {
          setLockoutMessage(errorResponse.error);
          setError(null);
        } else {
          setError(t("galleryGate.incorrectPassword"));
          setLockoutMessage(null);
        }
      } else {
        setError(t("galleryGate.genericError"));
      }
    }
  }, [tokenMutation.isError, tokenMutation.error]);

  const contrastColor = getContrastColor(galleryColor);

  return (
    <div
      id="channel-gate"
      className=" h-screen w-screen flex pt-72 items-center flex-col "
      style={{ background: galleryColor }}
    >
      <div
        className="flex flex-col gap-8 lg:min-w-[460px] text-white/70"
        style={{ color: contrastColor }}
      >
        <h1 className="font-inter text-3xl font-extrabold">
          {t("galleryGate.title")}
        </h1>
        <h2 className="text-base">
          {t("galleryGate.instruction")}
        </h2>
        {error || lockoutMessage ? <h3>{lockoutMessage || error}</h3> : null}
        <form onSubmit={handleSubmit} className="flex flex-col gap-8">
          <Input
            name="password"
            placeholder={t("galleryGate.placeholder")}
            value={password}
            label={t("galleryGate.label")}
            type="password"
            bgColor={galleryColor}
            labelInBorder
            onChange={(e) => setPassword(e.target.value)}
          />

          <button
            disabled={!password.length || tokenMutation.isPending}
            type="submit"
            className="btn-primary w-full disabled:bg-mb-dark-gray bg-white/70 uppercase tracking-wider text-black hover:text-black active:!text-black"
          >
            {t("galleryGate.submit")}
          </button>
        </form>
        <LegalLinks color={getAccessibleTextColor(galleryColor)} />
      </div>
    </div>
  );
};

export default GalleryGate;
